/**
 * Règles d'autorisation des actions sur une demande d'accompagnement.
 *
 * Source UNIQUE des gardes, réutilisée par la liste, le détail ET (en miroir) le
 * mock. Combine le niveau de permission (`can('ACCOMP_*')`), le statut de la
 * demande et le lien d'assignation avec l'utilisateur courant.
 *
 * Rappel niveaux (cf plan) :
 *   - ACCOMP_READ  : créer / voir ses demandes (commercial)
 *   - ACCOMP_WRITE : technicien (se prendre une demande, prise en charge, repasser la sienne)
 *   - ACCOMP_ADMIN : dispatcher (attribue / réaffecte n'importe quoi à n'importe qui)
 * SUPER_ADMIN est couvert par `can()` (renvoie true partout).
 */
import type { AccompAction, AccompRequest } from '@/v2/accompagnement/api/accompagnementTypes'

/** Surface minimale du store de permissions utilisée ici (facilite les tests). */
export interface PermLike {
  can(permission: string): boolean
}

function isClosed(req: AccompRequest): boolean {
  return req.status === 'GAGNEE' || req.status === 'PERDUE' || req.status === 'ANNULEE'
}

export function canCreate(perms: PermLike): boolean {
  return perms.can('ACCOMP_READ')
}

/**
 * Attribuer / réaffecter : réservé au dispatcher (ACCOMP_ADMIN). Le technicien ne
 * réaffecte plus — s'il ne peut pas honorer une demande, il la **refuse** (cf
 * `canRefuse`), ce qui la renvoie au dispatcher.
 */
export function canAssign(req: AccompRequest, perms: PermLike, _currentUserUuid: string): boolean {
  if (isClosed(req)) return false
  return perms.can('ACCOMP_ADMIN')
}

/** Le technicien se prend une demande encore non attribuée. */
export function canSelfTake(req: AccompRequest, perms: PermLike): boolean {
  return perms.can('ACCOMP_WRITE') && req.status === 'NOUVELLE' && !req.assignedToUuid
}

/**
 * Refuser : le technicien assigné, tant que la demande est attribuée (pas encore
 * prise en charge). Motif obligatoire ; renvoie la demande au dispatcher.
 */
export function canRefuse(req: AccompRequest, perms: PermLike, currentUserUuid: string): boolean {
  return (
    perms.can('ACCOMP_WRITE') && req.assignedToUuid === currentUserUuid && req.status === 'ATTRIBUEE'
  )
}

/** Relancer / redispatcher : le dispatcher sur une demande attribuée non prise en charge. */
export function canRemind(req: AccompRequest, perms: PermLike): boolean {
  return perms.can('ACCOMP_ADMIN') && req.status === 'ATTRIBUEE'
}

/** Prise en charge : le technicien assigné (ou l'admin) sur une demande ATTRIBUEE. */
export function canTakeCharge(req: AccompRequest, perms: PermLike, currentUserUuid: string): boolean {
  if (req.status !== 'ATTRIBUEE') return false
  if (perms.can('ACCOMP_ADMIN')) return true
  return perms.can('ACCOMP_WRITE') && req.assignedToUuid === currentUserUuid
}

/** Clôture : le technicien en charge (ou l'admin) sur une demande EN_COURS. */
export function canClose(req: AccompRequest, perms: PermLike, currentUserUuid: string): boolean {
  if (req.status !== 'EN_COURS') return false
  if (perms.can('ACCOMP_ADMIN')) return true
  return perms.can('ACCOMP_WRITE') && req.assignedToUuid === currentUserUuid
}

/** Annulation : le créateur (sa demande) ou l'admin, tant que non clôturée. */
export function canCancel(req: AccompRequest, perms: PermLike, currentUserUuid: string): boolean {
  if (isClosed(req)) return false
  if (perms.can('ACCOMP_ADMIN')) return true
  return perms.can('ACCOMP_READ') && req.createdByUuid === currentUserUuid
}

/**
 * Éditer le brief : l'admin (hors annulée) ; le technicien assigné tant que la
 * demande est en cours de traitement ; le créateur tant qu'elle n'est pas attribuée.
 */
export function canEdit(req: AccompRequest, perms: PermLike, currentUserUuid: string): boolean {
  if (req.status === 'ANNULEE') return false
  if (perms.can('ACCOMP_ADMIN')) return true
  if (
    perms.can('ACCOMP_WRITE') &&
    req.assignedToUuid === currentUserUuid &&
    (req.status === 'ATTRIBUEE' || req.status === 'EN_COURS')
  ) {
    return true
  }
  return perms.can('ACCOMP_READ') && req.createdByUuid === currentUserUuid && req.status === 'NOUVELLE'
}

/** Commenter : ouvert à tout profil ayant accès à l'accompagnement. */
export function canComment(perms: PermLike): boolean {
  return perms.can('ACCOMP_READ')
}

/** Journaliser une action de suivi : ouvert à tout profil ayant accès. */
export function canLogAction(perms: PermLike): boolean {
  return perms.can('ACCOMP_READ')
}

/** Supprimer une action : son auteur, ou un admin. */
export function canDeleteAction(action: AccompAction, perms: PermLike, currentUserUuid: string): boolean {
  return perms.can('ACCOMP_ADMIN') || action.createdByUuid === currentUserUuid
}

/**
 * Rattacher / détacher des devis : admin, technicien assigné, ou créateur.
 * Possible à tout moment (découplé de la clôture).
 */
export function canManageQuotes(req: AccompRequest, perms: PermLike, currentUserUuid: string): boolean {
  if (perms.can('ACCOMP_ADMIN')) return true
  return (
    (perms.can('ACCOMP_WRITE') && req.assignedToUuid === currentUserUuid) ||
    (perms.can('ACCOMP_READ') && req.createdByUuid === currentUserUuid)
  )
}
